/**
 * A mitr hands one vehicle to another mitr (owner, 13 Oct 2026: "each mitr or
 * dealer can also transfer vehicle to another mitra/dealer whenever they will
 * click a transfer button a delivery challan pdf will be downloaded in their
 * phone and they will select who so ever they will transfer this vehicle then
 * a approval request will be created will go to surbhi for approval once
 * surbhi approves the request the party who will get the transfer will also
 * get approval request he will tick i accept with all accesorries then the
 * transfer will be done and they can sell the vehicle").
 *
 * Three steps: the sender asks (and gets the delivery challan), the approver
 * says yes or no, the receiver accepts "with all accessories" or refuses.
 * Nothing moves until the receiver accepts. Mitr to mitr inside one book for
 * now — a dealer holds no stock (1 Oct 2026), and a transfer that crosses
 * books waits for the owner's word.
 */
import { ORG_IDS, type PartyType } from './domain.js';

export const VEHICLE_TRANSFER_STATUSES = [
  'PENDING_APPROVAL',
  'PENDING_ACCEPTANCE',
  'COMPLETED',
  'DECLINED',
  'REFUSED',
  'CANCELLED',
] as const;
export type VehicleTransferStatus = (typeof VEHICLE_TRANSFER_STATUSES)[number];

/** The two a vehicle is held by: it may not be sold or moved while one stands with its sender. */
export const VEHICLE_TRANSFER_LIVE = ['PENDING_APPROVAL', 'PENDING_ACCEPTANCE'] as const;

export function isLiveTransfer(status: string): boolean {
  return (VEHICLE_TRANSFER_LIVE as readonly string[]).includes(status);
}

export const VEHICLE_TRANSFER_STATUS_LABELS: Record<VehicleTransferStatus, string> = {
  PENDING_APPROVAL: 'Waiting for approval',
  PENDING_ACCEPTANCE: 'Waiting for the receiver',
  COMPLETED: 'Transferred',
  DECLINED: 'Not approved',
  REFUSED: 'Refused by the receiver',
  CANCELLED: 'Withdrawn',
};

/**
 * Who may send a transfer, by the sender's party type, and in which books.
 * Mitrs only, in the three books that keep mitrs; the receiver is always of
 * the sender's own type, in the sender's own book.
 */
export const TRANSFER_BOOKS: Partial<Record<PartyType, readonly string[]>> = {
  SUB_DEALER: [ORG_IDS.bow, ORG_IDS.fabev, ORG_IDS.tvs],
};

/** Whether a counter of this type, in this book, sends and receives transfers. */
export function transfersOpenTo(type: string | null | undefined, organizationId: string | null | undefined): boolean {
  if (!type || !organizationId) return false;
  return (TRANSFER_BOOKS[type as PartyType] ?? []).includes(organizationId);
}

/** Every book a transfer can happen in. */
export const TRANSFER_BOOK_IDS: readonly string[] = [...new Set(Object.values(TRANSFER_BOOKS).flat())];

/** The Indian financial year an instant falls in, April to March by the IST calendar: "26-27". */
export function challanFyLabel(at: Date): string {
  const ist = new Date(at.getTime() + 330 * 60_000);
  const year = ist.getUTCFullYear();
  const start = ist.getUTCMonth() >= 3 ? year : year - 1;
  const two = (n: number) => String(n % 100).padStart(2, '0');
  return `${two(start)}-${two(start + 1)}`;
}

/** "DC/26-27/00007" — the delivery challan's number. */
export function challanNumber(fy: string, serial: number): string {
  return `DC/${fy}/${String(serial).padStart(5, '0')}`;
}

/** The file a phone saves: no slashes, which a phone's file system will not take. */
export function challanFileName(challanNo: string): string {
  return `challan-${challanNo.replace(/[^A-Za-z0-9-]+/g, '-')}.pdf`;
}

/**
 * The company a book's delivery challan is issued in, as BUSY names it
 * (owner, 14 Oct 2026: each book its own — the paper he showed was
 * Autozone's). The TVS book is Bright on Wheels' dealership.
 */
export const BOOK_COMPANY_NAMES: Readonly<Record<string, string>> = {
  [ORG_IDS.wholesale]: 'BRIGHT AUTOZONE PRIVATE LIMITED',
  [ORG_IDS.bow]: 'BRIGHT ON WHEELS PRIVATE LIMITED',
  [ORG_IDS.tvs]: 'BRIGHT ON WHEELS PRIVATE LIMITED',
  [ORG_IDS.fabev]: 'FAB EV PRIVATE LIMITED',
};

/**
 * The challan the office's Transfer made for one hop (owner, 14 Oct 2026: "if
 * surbhi transfers a vehicle or anybody trasnfer a vehicle a delivery challan
 * is should be created by app automatically a pdf will be downloaded").
 */
export interface DeliveryChallanRef {
  id: string;
  challanNo: string;
  /** Who takes the vehicle — the "Customer Name" the challan prints. */
  toName: string;
  /** The handover's own instant, ISO. */
  occurredAt: string;
}

/** The file for several challans saved at once — one page each. */
export function challansFileName(refs: ReadonlyArray<Pick<DeliveryChallanRef, 'challanNo'>>): string {
  if (refs.length === 1) return challanFileName(refs[0]!.challanNo);
  return `challans-${refs.length}-${refs[0]!.challanNo.replace(/[^A-Za-z0-9-]+/g, '-')}.pdf`;
}

/** The words the receiver ticks (owner: "he will tick i accept with all accesorries"). */
export const TRANSFER_ACCEPT_WORDS = 'I accept this vehicle with all accessories';

/**
 * What the challan and the receiver's card list about the vehicle's build —
 * the dispatch form's answers, read from `Vehicle.extra` (completed from the
 * maker's twin) by the sheet's own column names.
 */
export const TRANSFER_SPEC_LINES: ReadonlyArray<{ label: string; headers: readonly string[] }> = [
  { label: 'Body', headers: ['flate bed cargo'] },
  { label: 'Charger', headers: ['charger model'] },
  { label: 'Battery', headers: ['battery company', 'battery ah'] },
  { label: 'Battery serial', headers: ['battery serial number'] },
  { label: 'Motor', headers: ['motor company', 'motor'] },
  { label: 'Differential', headers: ['differential'] },
  { label: 'Carrier', headers: ['carrier'] },
  { label: 'Alloy', headers: ['alloy'] },
  { label: 'Camera', headers: ['camera'] },
  { label: 'Engine no', headers: ['engineNo'] },
  { label: 'VIN', headers: ['vin'] },
];

/** A counter as a transfer shows it: no phone and no GSTIN on a screen. */
export interface TransferParty {
  id: string;
  name: string;
  city: string | null;
  district: string | null;
}

/** One transfer, as the sender, the receiver, the approver and the office read it. */
export interface VehicleTransferRow {
  id: string;
  status: VehicleTransferStatus;
  statusLabel: string;
  challanNo: string;
  organizationId: string;
  /** The book key a client sends as X-Book: "bow", "tvs", "fabev". */
  bookKey: string | null;
  bookName: string;
  note: string | null;
  createdAt: string;
  requestedBy: string;
  vehicle: {
    id: string;
    chassisNo: string;
    model: string;
    variant: string | null;
    color: string | null;
    /** The build, line by line; empty when the dispatch form recorded none. */
    spec: Array<{ label: string; value: string }>;
  };
  from: TransferParty;
  to: TransferParty;
  /** Who approved or declined, and when. */
  approval: { by: string | null; at: string; note: string | null } | null;
  /** Who accepted or refused, and when. */
  receiver: { by: string | null; at: string; note: string | null; withAllAccessories: boolean } | null;
  cancelled: { by: string | null; at: string | null; note: string | null } | null;
  /** A live transfer whose vehicle has left the sender since: it will be void at the next answer. */
  stale: boolean;
  /**
   * What the approver is deciding on (13 Oct 2026): how long the vehicle has
   * stood with the sender, and the sender's ledger. Sent to the approver only.
   */
  approverView?: {
    daysWithSender: number | null;
    ledger: { remaining: number; overdue: number; missing: number } | null;
  };
}

export const TRANSFER_LIST_STATES = ['pending', 'receiver', 'history'] as const;
export type TransferListState = (typeof TRANSFER_LIST_STATES)[number];
